import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SidebarMenu } from '@aimsos/ui';

/**
 * Core Shell side navigation.
 * Displays the sub-items of the currently selected top-level application.
 * Always visible — shows only the expand/collapse toggle when items is empty.
 *
 * Nested `items` use the shared Sidebar flyout (desktop) / accordion (mobile),
 * same as HTL Settings and `Sidebar.stories.jsx`.
 */
const ShellSideNav = ({ items = [], expanded, onToggleExpand }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const activeItem = useMemo(() => {
    const pathname = location.pathname;
    for (const item of items) {
      if (item.items?.some((child) => pathname.startsWith(child.id))) return item.id;
      if (item.id?.startsWith('/') && pathname.startsWith(item.id)) return item.id;
    }
    return '';
  }, [items, location.pathname]);

  const submenuActiveItem = useMemo(() => {
    const pathname = location.pathname;
    const matches = [];
    for (const item of items) {
      for (const child of item.items ?? []) {
        if (child.id && pathname.startsWith(child.id)) matches.push(child);
      }
    }
    return matches.sort((a, b) => b.id.length - a.id.length)[0]?.id ?? '';
  }, [items, location.pathname]);

  const handleItemClick = useCallback(
    (id) => {
      if (typeof id === 'string' && id.startsWith('/')) navigate(id);
    },
    [navigate],
  );

  const handleSubmenuItemClick = useCallback((id) => navigate(id), [navigate]);

  if (!items.length) return null;

  return (
    <SidebarMenu
      items={items}
      expanded={expanded}
      showToggle
      floating
      variant="default"
      activeItem={activeItem}
      onItemClick={handleItemClick}
      submenuOpen={submenuOpen}
      onSubmenuOpenChange={setSubmenuOpen}
      submenuActiveItem={submenuActiveItem}
      onSubmenuItemClick={handleSubmenuItemClick}
      onToggleExpand={onToggleExpand}
      collapsedWidth={54}
      className="[&>div]:!border-0"
    />
  );
};

export default ShellSideNav;
